import { Box, Button, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Download } from "@mui/icons-material";
import TextFormatter from "components/TextFormatter";
import { Question_Type, TrialResultQuery } from "@generated/graphql";
import { ReactNode, useContext, useState } from "react";
import { green, red, lime, yellow, orange } from "@mui/material/colors";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { Decision } from "./RecomendationBlock";
import MultipleChoiceAnswer from "./MultipleChoiceAnswer";
import { ExpandedTrialAnswer } from "../../helpers/getTrialAnswerList";

const Block = ({
  title,
  subtitle,
  data,
}: {
  title: string;
  subtitle?: string | number | ReactNode;
  data: string | number | ReactNode;
}) => {
  return (
    <Stack
      direction='row'
      spacing={2}>
      <Stack spacing={1}>
        <Box
          height='fit-content'
          width={140}
          sx={{
            backgroundColor: "base.50",
            borderRadius: 1,
            boxShadow: "0px 0px 7.3px 0px rgba(164, 184, 253, 0.24)",
            border: "1px solid",
            borderColor: "base.100",
            p: 1,
          }}>
          <Typography>{title}</Typography>
        </Box>
        {typeof subtitle === "string" ? <Typography>{subtitle}</Typography> : subtitle}
      </Stack>
      <Box
        flex={1}
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}>
        <Typography sx={{ whiteSpace: "pre-wrap", wordWrap: "anywhere" }}>{data}</Typography>
      </Box>
    </Stack>
  );
};

interface CandidateAnswerProps {
  trial: NonNullable<TrialResultQuery["trial"]>;
  answer: ExpandedTrialAnswer;
}

const CandidateAnswer = ({ trial, answer }: CandidateAnswerProps) => {
  const { addToast } = useContext(ToastContext);
  const [loading, setLoading] = useState(false);
  const { examination } = trial;

  const getDecisionColor = (decision: string | number | null) => {
    const decisionNumber = Number(decision);

    if (decisionNumber > 75) return green[900];
    if (decisionNumber <= 75 && decisionNumber > 50) return lime[900];
    if (decisionNumber <= 50 && decisionNumber > 25) return yellow[900];
    if (decisionNumber <= 25 && decisionNumber > 0) return orange[900];
    return red[900];
  };

  const getDecisionValue = (decision: string) => {
    if (decision === Decision.correct) return "100";
    if (decision === Decision.almost_correct) return "75";
    if (decision === Decision.partial_correct) return "50";
    if (decision === Decision.partial_incorrect) return "25";
    if (decision === Decision.incorrect) return "0";
    return decision;
  };

  const getDecisionResult = (decision: string): { text: string; color: string } => {
    const decisionValue = getDecisionValue(decision);

    return { text: `${decisionValue}%`, color: getDecisionColor(decisionValue) };
  };

  const handleAnswerFileButtonClick = (fileUrl: string) => {
    setLoading(true);

    fetch(fileUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        // eslint-disable-next-line max-len
        a.download = `${examination.title ? `${examination.title}_` : ""}${examination.numericId}_${trial.companyCandidate.fullName}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        addToast({ text: "Ошибка при скачивании файла", type: ToastTypeEnum.ERROR });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const answerFile = answer.file;

  const fields = [
    {
      title: "Компетенция",
      data: answer.blockTitle,
    },
    {
      title: "Блок знаний",
      data: answer.knowledgeBlockTitle,
    },
    {
      title: "Навык",
      data: answer.skillTitle,
    },
    {
      title: "Вопрос",
      data: <TextFormatter text={answer.questionText} />,
    },
    {
      title: "Ответ",
      subtitle: answerFile?.url ? (
        <Button
          variant='outlined'
          size='small'
          sx={{ paddingRight: 0.5, paddingLeft: 0.2 }}
          disabled={loading}
          onClick={() => handleAnswerFileButtonClick(answerFile.url)}>
          <Download
            fontSize='small'
            sx={{ fontSize: 18 }}
          />
          <Typography variant='body14rg'>Голосовой ответ</Typography>
        </Button>
      ) : (
        ""
      ),
      data:
        answer.questionType === Question_Type.MultipleChoiceQuestions ? (
          <MultipleChoiceAnswer
            questionParts={answer.questionParts}
            partsIds={answer.partsIds}
          />
        ) : (
          <TextFormatter text={answer.candidateAnswer || ""} />
        ),
    },
    {
      title: "Оценка ответа",
      data: answer.score?.toFixed(),
    },
    {
      title: "Время ответа",
      data: typeof answer.time === "number" ? new Date(answer.time * 1000).toISOString().substring(11, 19) : "",
    },
  ];

  return (
    <Grid
      container
      item
      xs={12}
      sx={{
        p: 2,
        backgroundColor: "white",
        boxShadow: "0px 0px 7.3px 0px rgba(164, 184, 253, 0.24)",
        borderRadius: 2,
      }}>
      <Grid
        container
        rowSpacing={2}>
        <Grid
          item
          xs={12}
          lg={8}>
          <Stack spacing={2}>
            {fields.map((field, key) => (
              <Block
                // eslint-disable-next-line react/no-array-index-key
                key={`answer-block-${key}`}
                {...field}
              />
            ))}
          </Stack>
        </Grid>
        {answer.questionType !== Question_Type.MultipleChoiceQuestions && (
          <Grid
            item
            xs={12}>
            <Table sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "white",
                  }}>
                  <TableCell sx={{ borderTopLeftRadius: 8 }}>Критерии оценки</TableCell>
                  <TableCell>Соответствие</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {answer.criterionAssessments?.map((criteria, key) => {
                  const decision = getDecisionResult(criteria.decision);
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={`answer-criteria-${key}`}>
                      <TableCell>{criteria.criteria}</TableCell>
                      <TableCell>
                        <Typography
                          variant='body14rg'
                          sx={{ color: decision.color }}>
                          {decision.text}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default CandidateAnswer;
